import { useState } from 'react';

import { useEmployees, useVendors } from '@/api/masters';
import { Field } from '@/components/Field';
import { SearchPicker, type PickerOption } from '@/components/SearchPicker';
import { languageNames } from '@/pages/print/narrativeText';

import { parseTailorKey, tailorKey } from './tailorRef';

/**
 * Who this tailor is, once one has been chosen: a name, and at most one of the two masters.
 *
 * <p>
 * The name is always filled and the two ids never both are — a check constraint on
 * `TblRentalOrderItem` enforces the second half, and this type is the shape that makes breaking it
 * awkward to express rather than merely refused at the far end.
 * </p>
 */
export interface ChosenTailor {
  name: string;
  vendorId: number | null;
  employeeId: number | null;
}

/**
 * Who has the garment — searched across both masters at once, or typed.
 *
 * <p>
 * <strong>One list holding the workshop down the lane and the karigar in the back room.</strong> A
 * clerk asks "who has it", not "is that person on the payroll", and until the employee master
 * existed only half the answer could be recorded: work kept in-house had a typed name and nothing
 * behind it, so a docket for the shop's own tailor could never print in his language and no job
 * could be assigned to him. Two pickers side by side would have made the clerk answer the
 * bookkeeping question first, which is exactly the wrong order.
 * </p>
 *
 * <p>
 * <strong>The ids are prefixed, and that is not tidiness.</strong> Both tables draw keys from the
 * same IDENTITY seed, so vendor 1000004 and employee 1000004 both exist and are different people;
 * merged on a bare number they would collide in React's keys and, worse, the choice read back could
 * attach the wrong master's row to the garment. `v:` and `e:` are what keep the two apart in one
 * list — the same collision `NamesABooking` guards on the server, arriving in the browser.
 * </p>
 *
 * <p>
 * <strong>Typing is not a fallback nobody uses.</strong> A shop sends a garment to whoever can take
 * it that afternoon, including somebody nobody has set up, and a counter that could not send one out
 * until a master record existed would stop working when it was busiest. So the name is what is
 * always recorded and a link is what is recorded when there is one.
 * </p>
 */
export function TailorPicker({
  name,
  vendorId,
  employeeId,
  branchId,
  disabled,
  onChange,
}: {
  name: string;
  vendorId: number | null;
  employeeId: number | null;
  branchId: number | undefined;
  disabled: boolean;
  /** All three together: picking sets a name and at most one link, and clearing drops the lot. */
  onChange: (next: ChosenTailor) => void;
}) {
  const [search, setSearch] = useState('');

  // Only tailors, on both sides. A launderer in this list is a wrong choice made easy, and the
  // capability column exists on both masters precisely so this question has one answer.
  const vendors = useVendors(branchId, { capability: 'Tailor', search, pageSize: 10 });
  const employees = useEmployees(branchId, { capability: 'Tailor', search, pageSize: 10 });

  /*
    Our own people first. It is the cheaper answer — no challan, no outside bill — and the one a
    shop should have to scroll past rather than hunt for, now that it can be recorded at all.
  */
  const options: PickerOption[] = [
    ...(employees.data?.items ?? []).map((employee) => ({
      id: tailorKey({ master: 'employee', id: employee.id }),
      primary: employee.fullName,
      secondary: `In-house · ${employee.mobile}`,

      // The language on the row, because it is what changes the printed sheet and the one fact a
      // clerk cannot recover after choosing.
      badge: employee.docketLanguage ? languageNames[employee.docketLanguage] : 'language not set',
    })),
    ...(vendors.data?.items ?? []).map((vendor) => ({
      id: tailorKey({ master: 'vendor', id: vendor.id }),
      primary: vendor.name,
      secondary: vendor.mobile,
      badge: vendor.docketLanguage ? languageNames[vendor.docketLanguage] : 'language not set',
    })),
  ];

  /*
    A typed name has no row to look up, so it is shown as a chosen card with no id behind it.
    Without this the field would go blank on reopening a garment whose tailor was typed — the name
    is on the garment, and the picker has to be able to display what it cannot find.
  */
  const selectedId =
    vendorId !== null
      ? tailorKey({ master: 'vendor', id: vendorId })
      : employeeId !== null
        ? tailorKey({ master: 'employee', id: employeeId })
        : null;

  const chosen: PickerOption | null =
    selectedId !== null
      ? (options.find((option) => option.id === selectedId) ?? { id: selectedId, primary: name })
      : name.trim() !== ''
        ? { id: 'typed', primary: name, badge: 'not on either list' }
        : null;

  return (
    <Field label="Tailor">
      <SearchPicker
        value={chosen}
        options={options}
        // Summed, because the panel's "keep typing to narrow" line is about the whole list a clerk
        // is looking at. Either master alone would understate it and stop offering the hint at the
        // point it is most needed.
        total={(vendors.data?.total ?? 0) + (employees.data?.total ?? 0)}
        loading={vendors.isLoading || employees.isLoading}
        disabled={disabled}
        placeholder="Search tailors, or type a name"
        onSearch={setSearch}
        onSelect={(option) => onChange(option === null ? cleared : unpack(option))}
        /*
          "Use what I typed" rather than "create a tailor". Creating a master record is a real act
          with a code, a mobile number and a scope, and a counter mid-alteration is the wrong place
          to be asked for them — so the typed name goes on the garment and the person gets set up
          properly later.
        */
        onCreateRequested={(typed) => {
          if (typed) {
            onChange({ name: typed, vendorId: null, employeeId: null });
          }
        }}
        createLabel={(typed) =>
          typed ? `Use “${typed}” — not on either list` : 'Or type a name that is not listed'
        }
      />
    </Field>
  );
}

const cleared: ChosenTailor = { name: '', vendorId: null, employeeId: null };

/**
 * Which master a chosen row came from, as the two nullable ids a garment stores.
 *
 * <p>
 * Private, so this file exports only its component — the real work is `parseTailorKey`, which
 * lives in `tailorRef.ts` and is where the test asks its questions.
 * </p>
 *
 * <p>
 * A row whose id carries neither prefix is one this picker did not build from a master — the
 * typed-name card is the only such row — so it resolves to a name with no link, which is exactly
 * what a typed name is. It does **not** guess at a master: attaching a garment to whichever table
 * happened to hold that number is the collision the prefixing exists to prevent.
 * </p>
 */
function unpack(option: PickerOption): ChosenTailor {
  const ref = parseTailorKey(option.id);

  return {
    name: option.primary,
    vendorId: ref?.master === 'vendor' ? ref.id : null,
    employeeId: ref?.master === 'employee' ? ref.id : null,
  };
}
